import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

// ISO (YYYY-MM-DD) -> DD-MON-YYYY (e.g. 08-SEP-2026)
export const fmtDate = (iso) => {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  const parts = s.split('-');
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts;
  const mi = parseInt(m, 10) - 1;
  if (mi < 0 || mi > 11) return iso;
  return `${d}-${MONTHS[mi]}-${y}`;
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const pad = (n) => String(n).padStart(2, '0');
const lastDay = (y, m) => new Date(y, m, 0).getDate(); // m is 1-based

// Indian FY that a given date falls in (returns start calendar year)
const fyStartYearOf = (d) => { const dt = new Date(d + 'T00:00:00'); return dt.getMonth() >= 3 ? dt.getFullYear() : dt.getFullYear() - 1; };
export const fyLabel = (sy) => `FY ${sy}-${String(sy + 1).slice(2)}`;

export const fyRange = (sy) => ({ from_date: `${sy}-04-01`, to_date: `${sy + 1}-03-31`, label: fyLabel(sy) });
export const quarterRange = (sy, q) => {
  const map = { 1: [sy, 4, sy, 6], 2: [sy, 7, sy, 9], 3: [sy, 10, sy, 12], 4: [sy + 1, 1, sy + 1, 3] };
  const [fy, fm, ty, tm] = map[q];
  return { from_date: `${fy}-${pad(fm)}-01`, to_date: `${ty}-${pad(tm)}-${lastDay(ty, tm)}`, label: `${fyLabel(sy)} · Q${q}` };
};
const monthRange = (ym) => { const [y, m] = ym.split('-').map(Number); return { from_date: `${y}-${pad(m)}-01`, to_date: `${y}-${pad(m)}-${lastDay(y, m)}`, label: `${MONTHS[m - 1]} ${y}` }; };
const lastWeekRange = () => {
  const t = new Date();
  const dow = (t.getDay() + 6) % 7; // Mon=0 … Sun=6
  const thisMon = new Date(t); thisMon.setDate(t.getDate() - dow);
  const lastMon = new Date(thisMon); lastMon.setDate(thisMon.getDate() - 7);
  const lastSun = new Date(lastMon); lastSun.setDate(lastMon.getDate() + 6);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from_date: iso(lastMon), to_date: iso(lastSun), label: `Last Week (${fmtDate(iso(lastMon))} → ${fmtDate(iso(lastSun))})` };
};

const currentFy = fyStartYearOf(todayStr());
const FY_YEARS = [currentFy - 2, currentFy - 1, currentFy, currentFy + 1];

export function PeriodSelector({ onChange, defaultType = 'day', defaultDay }) {
  const [ptype, setPtype] = useState(defaultType);
  const [day, setDay] = useState(defaultDay || todayStr());
  const [month, setMonth] = useState(todayStr().slice(0, 7));
  const [fy, setFy] = useState(currentFy);
  const [quarter, setQuarter] = useState(1);
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());

  const range = useMemo(() => {
    if (ptype === 'day') return { from_date: day, to_date: day, label: fmtDate(day) };
    if (ptype === 'week') return lastWeekRange();
    if (ptype === 'month') return monthRange(month);
    if (ptype === 'quarter') return quarterRange(fy, quarter);
    if (ptype === 'fy') return fyRange(fy);
    return { from_date: from, to_date: to, label: `${fmtDate(from)} → ${fmtDate(to)}` };
  }, [ptype, day, month, fy, quarter, from, to]);

  useEffect(() => { onChange?.(range); }, [range]); // eslint-disable-line

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <Label className="text-[11px] text-slate-500">Period</Label>
        <div className="flex rounded-md border border-slate-200 overflow-hidden mt-1" data-testid="period-type">
          {['day', 'week', 'month', 'quarter', 'fy', 'custom'].map((m) => (
            <button key={m} onClick={() => setPtype(m)} data-testid={`period-${m}`}
              className={`px-3 py-1.5 text-[12px] font-medium capitalize ${ptype === m ? 'bg-violet-600 text-white' : 'bg-white text-slate-600'}`}>{m === 'fy' ? 'FY' : m === 'week' ? 'Last Week' : m}</button>
          ))}
        </div>
      </div>
      {ptype === 'day' && <div><Label className="text-[11px] text-slate-500">Date</Label><Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-9 mt-1 w-40" data-testid="period-date" /></div>}
      {ptype === 'month' && <div><Label className="text-[11px] text-slate-500">Month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 mt-1 w-40" data-testid="period-month" /></div>}
      {(ptype === 'quarter' || ptype === 'fy') && (
        <div><Label className="text-[11px] text-slate-500">Financial Year</Label>
          <Select value={String(fy)} onValueChange={(v) => setFy(Number(v))}><SelectTrigger className="h-9 mt-1 w-36" data-testid="period-fy"><SelectValue /></SelectTrigger>
            <SelectContent>{FY_YEARS.map((y) => <SelectItem key={y} value={String(y)}>{fyLabel(y)}</SelectItem>)}</SelectContent></Select></div>
      )}
      {ptype === 'quarter' && (
        <div><Label className="text-[11px] text-slate-500">Quarter</Label>
          <Select value={String(quarter)} onValueChange={(v) => setQuarter(Number(v))}><SelectTrigger className="h-9 mt-1 w-40" data-testid="period-quarter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Q1 (Apr–Jun)</SelectItem><SelectItem value="2">Q2 (Jul–Sep)</SelectItem>
              <SelectItem value="3">Q3 (Oct–Dec)</SelectItem><SelectItem value="4">Q4 (Jan–Mar)</SelectItem>
            </SelectContent></Select></div>
      )}
      {ptype === 'custom' && <>
        <div><Label className="text-[11px] text-slate-500">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 mt-1 w-36" data-testid="period-from" /></div>
        <div><Label className="text-[11px] text-slate-500">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 mt-1 w-36" data-testid="period-to" /></div>
      </>}
    </div>
  );
}
